import { useMemo, useState } from "react";
import { AlertTriangle, Thermometer } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, useApi, fmtDT, submitOrQueue, useQueue } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Field, Pill, Table, Th, Td, Card } from "@/components/kit";
import { PhotoPicker, Photos } from "@/components/PhotoPicker";

const PROBE_KINDS = ["cooking", "reheating", "cooling", "hot_holding", "delivery_chilled", "delivery_frozen"];
const EQ_KIND = { fridge: "fridge", freezer: "freezer", hot_holding: "hot_holding" };

function TempForm({ equipment, kinds, onSaved }) {
  const { venueId, can, user } = useAuth();
  const [target, setTarget] = useState(null);
  const [value, setValue] = useState("");
  const [item, setItem] = useState("");
  const [action, setAction] = useState("");
  const [food, setFood] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const limits = useMemo(() => {
    if (!target) return {};
    const k = kinds?.[target.kind] || {};
    const eq = target.equipment;
    return { min: eq?.min_temp ?? k.min, max: eq?.max_temp ?? k.max };
  }, [target, kinds]);
  const v = value === "" ? null : Number(value);
  const bad = v != null && ((limits.min != null && v < limits.min) || (limits.max != null && v > limits.max));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const label = target.equipment?.name || item || kinds?.[target.kind]?.label || target.kind;
      const res = await submitOrQueue("/temps", { venue_id: venueId, kind: target.kind, equipment_id: target.equipment?.id || null, item, value: v, action_taken: action, food_disposition: food, photo_ids: photos, measured_at: new Date().toISOString() },
        { user_id: user.id, kind: "temp", label: `${label} ${v}°C`, venue_id: venueId, value: v, out_of_range: bad, what: label });
      if (res.queued) toast.info("Saved on this device — it will sync when you're back online");
      else toast[bad ? "warning" : "success"](bad ? "Out-of-range reading saved. Manager alerted via incident." : "Reading saved");
      setValue(""); setItem(""); setAction(""); setFood(""); setPhotos([]);
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  if (!can("record")) return null;
  const chip = (key, label, t) => (
    <button key={key} type="button" data-testid={`temp-target-${key}`} onClick={() => setTarget(t)}
      className={`ka-press rounded-xl border px-4 py-3 text-left text-sm font-semibold ${target && (target.equipment?.id || target.kind) === key ? "border-emerald-600 bg-emerald-600 text-white" : "border-slate-200 bg-white text-slate-800 hover:border-slate-400"}`}>{label}</button>
  );

  return (
    <Card className="mb-8">
      <div className="ka-eyebrow mb-3">1 · What are you checking?</div>
      <div className="flex flex-wrap gap-2">
        {equipment.filter((e) => EQ_KIND[e.type]).map((e) => chip(e.id, e.name, { kind: EQ_KIND[e.type], equipment: e }))}
        {PROBE_KINDS.map((k) => chip(k, kinds?.[k]?.label || k, { kind: k }))}
      </div>
      {target && (
        <form onSubmit={submit} className="mt-6 space-y-4 ka-fade">
          <div className="ka-eyebrow">2 · Reading {limits.min != null && `· min ${limits.min}°C`} {limits.max != null && `· max ${limits.max}°C`}</div>
          <div className="grid gap-4 sm:grid-cols-2">
            <input data-testid="temp-log-value-input" required type="number" step="0.1" inputMode="decimal" placeholder="°C" value={value} onChange={(e) => setValue(e.target.value)}
              className={`ka-input h-16 text-3xl font-bold ${bad ? "border-rose-500 text-rose-700" : ""}`} />
            {!target.equipment && <input data-testid="temp-log-item-input" className="ka-input h-16" placeholder="Food / batch (e.g. chicken curry)" value={item} onChange={(e) => setItem(e.target.value)} />}
          </div>
          {bad && (
            <div data-testid="temp-out-of-range-panel" className="space-y-3 rounded-xl border border-amber-300 bg-amber-50 p-4">
              <div className="flex items-center gap-2 font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" /> Out of range — record what you did now</div>
              <Field label="Immediate action taken *"><textarea data-testid="temp-action-input" required rows={2} className="ka-input" value={action} onChange={(e) => setAction(e.target.value)} placeholder="e.g. moved stock to walk-in, adjusted thermostat, re-cooked to 75°C" /></Field>
              <Field label="What happened to the food? *"><textarea data-testid="temp-food-input" required rows={2} className="ka-input" value={food} onChange={(e) => setFood(e.target.value)} placeholder="e.g. discarded 2kg chicken / safe — under 2h above 8°C" /></Field>
              <p className="text-xs text-amber-800">Raising a maintenance job is not enough on its own — the food decision must be recorded.</p>
            </div>
          )}
          <Field label="Photo evidence (optional)" hint="Snap the thermometer display or probe reading"><PhotoPicker testid="temp-photo" allowOffline value={photos} onChange={setPhotos} /></Field>
          <button data-testid="submit-temp-log-button" disabled={busy} className="ka-press w-full rounded-full bg-emerald-600 py-3.5 text-sm font-semibold text-white hover:bg-emerald-700 sm:w-auto sm:px-10">{busy ? "Saving…" : "Save reading"}</button>
        </form>
      )}
    </Card>
  );
}

export default function Temps() {
  const { venueId, venue, user } = useAuth();
  const { data: equipment } = useApi(venueId ? "/equipment" : null, { venue_id: venueId });
  const { data: kinds } = useApi("/temps/kinds");
  const { data: logs, reload } = useApi(venueId ? "/temps" : null, { venue_id: venueId, days: 14 });
  const pending = useQueue().filter((i) => i.kind === "temp" && i.venue_id === venueId && i.user_id === user.id);
  return (
    <div>
      <PageHeader eyebrow={venue?.name} title="Temperatures" subtitle="Fridges, freezers, cooking, reheating, cooling, hot holding and deliveries — checked against UK limits." />
      <TempForm equipment={equipment || []} kinds={kinds} onSaved={reload} />
      <h3 className="mb-3 flex items-center gap-2 text-lg font-bold"><Thermometer className="h-4 w-4" />Last 14 days</h3>
      <Table testid="temp-logs-table">
        <thead><tr><Th>Measured</Th><Th>What</Th><Th>Reading</Th><Th>Limit</Th><Th>Recorded by</Th><Th>Action / photos</Th></tr></thead>
        <tbody>
          {pending.map((p) => (
            <tr key={p.id} data-testid={`temp-pending-${p.id}`} className="bg-sky-50/60">
              <Td className="font-mono text-xs">{fmtDT(p.body.measured_at)}</Td>
              <Td className="font-medium text-slate-900">{p.what}</Td>
              <Td><Pill status={p.out_of_range ? "overdue" : "ok"} label={`${p.value}°C`} /></Td>
              <Td>—</Td>
              <Td>{user.name}</Td>
              <Td><Pill status="pending_sync" label="Waiting to sync" /></Td>
            </tr>
          ))}
          {(logs || []).map((t) => (
            <tr key={t.id} data-testid={`temp-row-${t.id}`} className={t.out_of_range ? "bg-rose-50/60" : ""}>
              <Td className="font-mono text-xs">{fmtDT(t.measured_at)}</Td>
              <Td><div className="font-medium text-slate-900">{t.equipment_name || t.item || kinds?.[t.kind]?.label}</div><div className="text-xs text-slate-500">{kinds?.[t.kind]?.label}</div></Td>
              <Td><Pill status={t.out_of_range ? "overdue" : "ok"} label={`${t.value}°C`} /></Td>
              <Td className="font-mono text-xs">{t.min != null ? `≥${t.min}` : ""} {t.max != null ? `≤${t.max}` : ""}</Td>
              <Td>{t.recorded_by}{t.recorded_offline && <div className="text-[11px] text-sky-700">synced later</div>}</Td>
              <Td className="max-w-xs text-xs">{t.action_taken ? `${t.action_taken} · Food: ${t.food_disposition}` : "—"}<div className="mt-1"><Photos ids={t.photo_ids} size="h-10 w-10" /></div></Td>
            </tr>
          ))}
          {logs && !logs.length && <tr><Td className="text-slate-500">No readings yet.</Td></tr>}
        </tbody>
      </Table>
    </div>
  );
}
